import React, { useEffect, useState, useCallback } from 'react';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import connectionStatus, { SERVER_NOT_REACHABLE, SERVER_REACHABLE } from '../../connectionStatus';
import SyncEngine from '../../db/sync/SyncEngine';
import * as PendingRepository from '../../db/repositories/PendingRepository';
import { useAuth } from '../../util/authContext';

const OfflineBar = () => {
  const [showOffline, setShowOffline] = useState(!connectionStatus.isServerReachable());
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncHealthy, setSyncHealthy] = useState(true);
  const [retryingInit, setRetryingInit] = useState(false);
  const { initError, retryInit } = useAuth();

  const checkSyncHealth = useCallback(async () => {
    try {
      const count = await PendingRepository.getCount();
      setPendingCount(count);
      const status = SyncEngine.getSyncStatus();
      setSyncHealthy(status.healthy);
    } catch {
      // DB may not be initialized yet
    }
  }, []);

  useEffect(() => {
    const onReachable = () => setShowOffline(false);
    const onNotReachable = () => setShowOffline(true);

    connectionStatus.on(SERVER_REACHABLE, onReachable);
    connectionStatus.on(SERVER_NOT_REACHABLE, onNotReachable);

    const syncInterval = setInterval(checkSyncHealth, 5000);
    checkSyncHealth();

    return () => {
      connectionStatus.removeListener(SERVER_REACHABLE, onReachable);
      connectionStatus.removeListener(SERVER_NOT_REACHABLE, onNotReachable);
      clearInterval(syncInterval);
    };
  }, [checkSyncHealth]);

  const handleRetry = (e) => {
    e.preventDefault();
    setCheckingConnection(true);
    setTimeout(() => {
      connectionStatus.testIsReachable()
        .then(() => setCheckingConnection(false))
        .catch(() => setCheckingConnection(false));
    }, 500);
  };

  const handleRetryInit = (e) => {
    e.preventDefault();
    setRetryingInit(true);
    // Re-check connectivity too, so the offline indicator clears alongside
    // a successful init retry.
    connectionStatus.testIsReachable().catch(() => {});
    Promise.resolve(retryInit()).finally(() => setRetryingInit(false));
  };

  const showSyncWarning = !showOffline && !syncHealthy && pendingCount > 0;
  const showInitError = !!initError;

  return (
    <>
      <Snackbar
        open={showOffline}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ mb: 1, mr: 1 }}
      >
        <Alert
          severity="error"
          icon={checkingConnection ? <CircularProgress size={16} color="inherit" /> : <WifiOffIcon fontSize="small" />}
          sx={{ alignItems: 'center', minWidth: 220 }}
          action={
            !checkingConnection && (
              <a
                href="#retry-connection"
                onClick={handleRetry}
                style={{ color: 'inherit', fontSize: '12px', whiteSpace: 'nowrap' }}
              >
                Retry
              </a>
            )
          }
        >
          Offline
        </Alert>
      </Snackbar>

      <Snackbar
        open={showInitError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          icon={retryingInit ? <CircularProgress size={16} color="inherit" /> : <LockOutlinedIcon fontSize="small" />}
          sx={{ alignItems: 'center', maxWidth: 520 }}
          action={
            !retryingInit && (
              <a
                href="#retry-init"
                onClick={handleRetryInit}
                style={{ color: 'inherit', fontSize: '12px', whiteSpace: 'nowrap' }}
              >
                Retry
              </a>
            )
          }
        >
          Your cases can't be shown yet — {initError} This will retry
          automatically once the server can be reached.
        </Alert>
      </Snackbar>

      <Snackbar
        open={showSyncWarning}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        sx={{ mb: showOffline ? 8 : 1, mr: 1 }}
      >
        <Alert
          severity="warning"
          icon={<SyncProblemIcon fontSize="small" />}
          sx={{ alignItems: 'center', minWidth: 220 }}
          action={
            <a
              href="#retry-sync"
              onClick={(e) => { e.preventDefault(); SyncEngine.processPendingQueue(); }}
              style={{ color: 'inherit', fontSize: '12px', whiteSpace: 'nowrap' }}
            >
              Retry
            </a>
          }
        >
          {pendingCount} unsaved change{pendingCount !== 1 ? 's' : ''}
        </Alert>
      </Snackbar>
    </>
  );
};

export default OfflineBar;
